import React, { useState, useRef, useEffect } from "react";
import TopicSelector from "./TopicSelector";
import TopicInput from ".//TopicInput";
import SubmitMessage from "../SubmitMessage";
import config from "./questionnaireConfig.json";
import { StepStatus } from "../../lib/hooks/useFormFlow";

interface QuestionnaireProps {
  onSubmit: (topic: Topic, message: string) => void;
  visible?: boolean;
  uploadedPhotoUrl: string;
  onBackToPhoto: () => void;
  onSkip: () => void;
  childName: string;
  onQuestionnaireView?: (topic: Topic) => void;
  onMessageInteractionStart?: (topic: Topic) => void;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  onClearError?: () => void;
  status: StepStatus;
  setStatus: (status: StepStatus) => void;
  selectedTopic: Topic | "";
  setSelectedTopic: (topic: Topic | "") => void;
  isDesktop?: boolean;
  isWideDesktop?: boolean;
}

// Convert the config to a const array for proper type inference
const TOPICS = config.topics as readonly {
  id: string;
  label: string;
  icon: string;
  question: string;
  template: string;
}[];

type Topic = (typeof TOPICS)[number]["id"];

function getTopicQuestion(topic: Topic, childName = "your sponsored child"): string {
  const topicConfig = TOPICS.find((t) => t.id === topic);
  if (!topicConfig) return "";

  return topicConfig.question.replace(/\{childName\}/g, childName);
}

/**
 * The Questionnaire component handles the two-step message creation flow:
 * 1. Topic selection - User chooses a conversation topic
 * 2. Message input - User writes a personalized message
 *
 * The component has different layouts for mobile and desktop:
 * - Mobile: Shows either topic selection OR message input
 * - Desktop: Shows both with topic selection above message input in step 2
 */
const Questionnaire: React.FC<QuestionnaireProps> = ({
  onSubmit,
  visible = true,
  onBackToPhoto,
  onSkip,
  childName,
  onQuestionnaireView,
  onMessageInteractionStart,
  isSubmitting = false,
  errorMessage = null,
  onClearError,
  status,
  setStatus,
  selectedTopic,
  setSelectedTopic,
  isDesktop = false,
  isWideDesktop = false
}) => {
  const [message, setMessage] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const previousStatusRef = useRef<StepStatus>(status);

  // Track which step in the questionnaire flow we're on
  // This determines which UI components to show
  const isTopicStep = status === "questionnaire-topic";
  const isInputStep = status === "questionnaire-input";

  useEffect(() => {
    if (errorMessage && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [errorMessage]);

  useEffect(() => {
    // Only clear errors when the status actually changes
    if (onClearError && previousStatusRef.current !== status) {
      onClearError();
    }

    // Update the ref to track the current status
    previousStatusRef.current = status;
  }, [status, onClearError]);

  if (!visible) return null;

  return (
    <div
      className={`post-checkout-flow__wrapper questionnaire ${isDesktop ? "desktop-view" : ""}`}
      ref={containerRef}
    >
      <div className="sak-engagement-flow__header">
        <h2 className="sak-engagement-flow__header-text">{config.ui.title}</h2>
      </div>

      <div className="sak-engagement-flow__body">
        <p>{config.ui.description}</p>
      </div>

      {errorMessage && (
        <>
          <SubmitMessage
            visible={true}
            alertType="danger"
            message={errorMessage}
            wrapperClassName=""
            className="submit-message questionnaire"
          />
        </>
      )}

      {/* Mobile View - Shows either topic selection OR message input, never both */}
      {!isDesktop && (
        <>
          {isTopicStep && (
            <TopicSelector
              topics={TOPICS}
              onTopicSelect={(topicId) => {
                const topic = topicId as Topic;
                setSelectedTopic(topic); // Update the app-level state
                setMessage(""); // Clear the message whenever a topic is chosen
                setStatus("questionnaire-input");

                // Call the handler to track the view when a topic is selected
                if (onQuestionnaireView) {
                  onQuestionnaireView(topic);
                }
              }}
              onBack={() => {
                onBackToPhoto();
              }}
              onSkip={onSkip}
              backToPhotoText={config.ui.backToPhotoText}
              skipButtonText={config.ui.skipButtonText}
              isDesktop={isDesktop}
              isWideDesktop={isWideDesktop}
            />
          )}

          {isInputStep && selectedTopic && (
            <>
              <TopicInput
                topic={selectedTopic}
                topicIcon={TOPICS.find((t) => t.id === selectedTopic)?.icon || ""}
                question={getTopicQuestion(selectedTopic, childName)}
                message={message}
                onMessageChange={setMessage}
                onBackToTopics={() => {
                  setStatus("questionnaire-topic");
                }}
                onMessageInteractionStart={() => {
                  if (onMessageInteractionStart && selectedTopic) {
                    onMessageInteractionStart(selectedTopic);
                  }
                }}
                disabled={isSubmitting}
                characterLimit={config.ui.textAreaCharacterLimit}
                backToTopicsText={config.ui.backToTopicsText}
              />
              <div className="sak-engagement-flow__controls">
                <button
                  className="btn btn-primary"
                  onClick={() => onSubmit(selectedTopic, message)}
                  disabled={message.trim().length === 0 || isSubmitting}
                >
                  {isSubmitting ? config.ui.submitButtonTextSubmitting : config.ui.submitButtonText}
                </button>

                <button onClick={onSkip} className="btn-link" disabled={isSubmitting}>
                  {config.ui.skipButtonText}
                </button>
              </div>
            </>
          )}
        </>
      )}

      {/* Desktop View - Enhanced UX showing topic selection above message input in step 2 */}
      {isDesktop && (
        <>
          {isTopicStep && (
            <TopicSelector
              topics={TOPICS}
              onTopicSelect={(topicId) => {
                const topic = topicId as Topic;
                setSelectedTopic(topic); // Update the app-level state
                setMessage(""); // Clear the message whenever a topic is chosen
                setStatus("questionnaire-input");

                // Call the handler to track the view when a topic is selected
                if (onQuestionnaireView) {
                  onQuestionnaireView(topic);
                }
              }}
              onBack={() => {
                onBackToPhoto();
              }}
              onSkip={onSkip}
              backToPhotoText={config.ui.backToPhotoText}
              skipButtonText={config.ui.skipButtonText}
              isDesktop={isDesktop}
              isWideDesktop={isWideDesktop}
            />
          )}

          {isInputStep && selectedTopic && (
            <div className="desktop-questionnaire-container">
              {/* Always show topic selector in desktop input mode, just above the input */}
              <div className="topic-selector-container">
                <TopicSelector
                  topics={TOPICS}
                  onTopicSelect={(topicId) => {
                    const topic = topicId as Topic;
                    setSelectedTopic(topic); // Update the app-level state
                    setMessage(""); // Clear the message whenever a topic is chosen

                    // Call the handler to track the view when a topic is selected
                    if (onQuestionnaireView) {
                      onQuestionnaireView(topic);
                    }
                  }}
                  onBack={() => {
                    onBackToPhoto();
                  }}
                  onSkip={onSkip}
                  backToPhotoText={config.ui.backToPhotoText}
                  skipButtonText={config.ui.skipButtonText}
                  selectedTopic={selectedTopic}
                  hideButtons={true}
                  isDesktop={isDesktop}
                  isWideDesktop={isWideDesktop}
                />
              </div>

              {/* Topic input area */}
              <div className="topic-input-container">
                <TopicInput
                  topic={selectedTopic}
                  topicIcon={TOPICS.find((t) => t.id === selectedTopic)?.icon || ""}
                  question={getTopicQuestion(selectedTopic, childName)}
                  message={message}
                  onMessageChange={setMessage}
                  onBackToTopics={() => {
                    setStatus("questionnaire-topic");
                  }}
                  onMessageInteractionStart={() => {
                    if (onMessageInteractionStart && selectedTopic) {
                      onMessageInteractionStart(selectedTopic);
                    }
                  }}
                  disabled={isSubmitting}
                  characterLimit={config.ui.textAreaCharacterLimit}
                  backToTopicsText={config.ui.backToTopicsText}
                  hideBackButton={true}
                />
                <div className="sak-engagement-flow__controls">
                  <button
                    className="btn btn-primary"
                    onClick={() => onSubmit(selectedTopic, message)}
                    disabled={message.trim().length === 0 || isSubmitting}
                  >
                    {isSubmitting
                      ? config.ui.submitButtonTextSubmitting
                      : config.ui.submitButtonText}
                  </button>

                  <button onClick={onSkip} className="btn-link" disabled={isSubmitting}>
                    {config.ui.skipButtonText}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Questionnaire;
